import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router";
import { type AgentTaskPhase, type AgentTaskRepository } from "@zworks/console-contracts";
import {
  EmptyState,
  ErrorState,
  ForbiddenState,
  LoadingState,
  NotConnectedState,
} from "@/components/states";
import { formatBytes, formatCredits, formatZonedDateTime } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/bespoke";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  agentTaskArtifactFileUrl,
  agentTaskArtifactsQuery,
  agentTaskDetailQuery,
  ApiError,
  cancelAgentTask,
  sendAgentTaskFeedback,
  startAgentTask,
} from "@/lib/api";
import { useCurrentProject } from "@/lib/project";

const phaseBadges: Record<
  AgentTaskPhase,
  { label: string; variant: "success" | "info" | "warning" | "danger" | "unknown" | "outline" }
> = {
  starting: { label: "Starting", variant: "info" },
  running: { label: "Running", variant: "info" },
  idle: { label: "Waiting for you", variant: "warning" },
  recovering: { label: "Recovering", variant: "warning" },
  done: { label: "Done", variant: "success" },
  cancelled: { label: "Cancelled", variant: "outline" },
  failed: { label: "Failed", variant: "danger" },
  unknown: { label: "Status unavailable", variant: "unknown" },
};

const activeTaskPhases: AgentTaskPhase[] = ["starting", "running", "idle", "recovering"];

export function PhaseBadge({ phase }: { phase: AgentTaskPhase }) {
  const badge = phaseBadges[phase];
  return <Badge variant={badge.variant}>{badge.label}</Badge>;
}

function problemText(error: unknown, fallback: string): string {
  return error instanceof ApiError ? (error.problem?.detail ?? error.message) : fallback;
}

function Heading({ title }: { title: string }) {
  return <PageHeader title={title} />;
}

function ProjectGate({
  title,
  loading,
  error,
  empty,
  onRetry,
  children,
}: {
  title: string;
  loading: boolean;
  error: unknown;
  empty: boolean;
  onRetry: () => void;
  children: React.ReactNode;
}) {
  if (loading) {
    return (
      <>
        <Heading title={title} />
        <LoadingState label="Loading agent tasks" rows={4} />
      </>
    );
  }
  if (empty) {
    return (
      <>
        <Heading title={title} />
        <EmptyState title="No projects yet" description="Create a project to run agent tasks." />
      </>
    );
  }
  if (error) {
    const status = error instanceof ApiError ? error.status : 0;
    return (
      <>
        <Heading title={title} />
        {status === 404 ? (
          <ForbiddenState
            title="You do not have access to this project"
            description="Ask an owner to invite you. Nothing from the project is shown."
          />
        ) : status === 409 || status === 503 ? (
          <EmptyState
            title={
              status === 409 ? "Agent tasks are not connected" : "Agent tasks are not switched on"
            }
            description={problemText(error, "The agent runtime is not available for this project.")}
          />
        ) : (
          <ErrorState
            title="Agent tasks could not be loaded"
            description={problemText(error, "The request could not be completed.")}
            requestId={error instanceof ApiError ? (error.requestId ?? "unknown") : "unknown"}
            onRetry={onRetry}
          />
        )}
      </>
    );
  }
  return <>{children}</>;
}

export function StartForm({
  projectId,
  repositories,
  branchPrefix,
  modelKeys,
}: {
  projectId: string;
  repositories: AgentTaskRepository[];
  branchPrefix: string;
  modelKeys: string[];
}) {
  const client = useQueryClient();
  const navigate = useNavigate();
  const [repositoryId, setRepositoryId] = useState(repositories[0]?.repositoryId ?? "");
  const [branch, setBranch] = useState(branchPrefix);
  const [prompt, setPrompt] = useState("");
  const [modelKeyRef, setModelKeyRef] = useState("");
  // One key per form, so a retry after a dropped response reuses the task.
  const [requestId] = useState(() => crypto.randomUUID());
  const start = useMutation({
    mutationFn: () =>
      startAgentTask(projectId, {
        repositoryId,
        branch: branch.trim(),
        prompt,
        requestId,
        ...(modelKeyRef === "" ? {} : { modelKeyRef }),
      }),
    onSuccess: (task) => {
      void client.invalidateQueries({ queryKey: ["projects", projectId, "agent-tasks"] });
      navigate(`/agent-tasks/${task.id}`);
    },
  });
  const ready =
    repositoryId !== "" &&
    branch.trim().startsWith(branchPrefix) &&
    branch.trim().length > branchPrefix.length &&
    prompt.trim() !== "";

  return (
    <Card>
      <CardContent className="flex flex-col gap-4">
        <div>
          <h2 className="text-base font-semibold">Start an agent task</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            The agent works on a branch under <code className="font-mono">{branchPrefix}</code> and
            can never push to the default branch.
          </p>
        </div>
        {repositories.length === 0 ? (
          <NotConnectedState
            title="Agent repositories are not connected yet"
            description="The console repository list above does not feed agent tasks. Until repositories are bound to the agent runtime, there is nowhere to give an agent work."
          />
        ) : (
          <form
            className="flex flex-col gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              if (ready) start.mutate();
            }}
          >
            <label className="flex flex-col gap-1 text-sm font-medium">
              Repository
              <select
                className="h-9 rounded-md border border-input bg-card px-3 text-sm font-normal"
                value={repositoryId}
                onChange={(event) => setRepositoryId(event.target.value)}
              >
                {repositories.map((repository) => (
                  <option key={repository.repositoryId} value={repository.repositoryId}>
                    {repository.owner}/{repository.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm font-medium">
              Branch
              <Input
                className="font-mono font-normal"
                value={branch}
                onChange={(event) => setBranch(event.target.value)}
                spellCheck={false}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm font-medium">
              What should the agent do?
              <Textarea
                className="min-h-32 font-normal"
                value={prompt}
                maxLength={20000}
                onChange={(event) => setPrompt(event.target.value)}
              />
            </label>
            {modelKeys.length > 0 ? (
              <label className="flex flex-col gap-1 text-sm font-medium">
                Model key (optional)
                <select
                  aria-label="Model key (optional)"
                  className="h-9 rounded-md border border-input bg-card px-3 text-sm font-normal"
                  value={modelKeyRef}
                  onChange={(event) => setModelKeyRef(event.target.value)}
                >
                  <option value="">Bill credits (default)</option>
                  {modelKeys.map((ref) => (
                    <option key={ref} value={ref}>
                      {ref} – no credits consumed
                    </option>
                  ))}
                </select>
                <span className="text-xs font-normal text-muted-foreground">
                  The run is metered from its turns but settles no credits. Only the reference is
                  stored; the key itself never leaves the credential store.
                </span>
              </label>
            ) : null}
            {start.isError ? (
              <p role="alert" className="text-sm text-danger">
                {problemText(start.error, "The task could not be started.")}
              </p>
            ) : null}
            <div>
              <Button type="submit" disabled={!ready || start.isPending}>
                {start.isPending ? "Starting" : "Start task"}
              </Button>
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Backwards-compatible alias: the standalone list moved into Runs. The route redirects
 * before this renders, so this only covers direct mounts in tests.
 */
export function AgentTasksPage() {
  return <Navigate to="/runs?type=agent" replace />;
}

/**
 * The results the control plane stored for the task, one sealed revision per finished turn. Hidden
 * when the console has task data switched off (the API answers 503).
 */
function TaskResults({
  projectId,
  taskId,
  refreshKey,
}: {
  projectId: string;
  taskId: string;
  refreshKey: number;
}) {
  const artifacts = useQuery({
    ...agentTaskArtifactsQuery(projectId, taskId),
    queryKey: [...agentTaskArtifactsQuery(projectId, taskId).queryKey, refreshKey],
    retry: false,
  });
  if (artifacts.error instanceof ApiError && artifacts.error.status === 503) return null;
  return (
    <section aria-labelledby="agent-results-heading" className="flex flex-col gap-3">
      <h2 id="agent-results-heading" className="text-base font-semibold">
        Results
      </h2>
      {artifacts.isPending ? (
        <p className="text-sm text-muted-foreground">Loading stored results…</p>
      ) : artifacts.isError ? (
        <p className="text-sm text-muted-foreground">
          Stored results could not be loaded right now.
        </p>
      ) : artifacts.data.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Results are stored here as sealed artifacts once a turn finishes.
        </p>
      ) : (
        <ul className="divide-y rounded-md border">
          {artifacts.data.map((artifact) => (
            <li key={artifact.revisionId} className="flex flex-col gap-2 p-4">
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <Badge variant={artifact.state === "sealed" ? "success" : "outline"}>
                  {artifact.state}
                </Badge>
                <span className="font-mono">{artifact.revisionId}</span>
                {artifact.turnId ? <span>turn {artifact.turnId}</span> : null}
                {artifact.createdAt ? (
                  <span>{formatZonedDateTime(artifact.createdAt, "UTC")}</span>
                ) : null}
              </div>
              <div className="flex flex-wrap gap-3">
                {artifact.files.map((file) => (
                  <a
                    key={file.path}
                    className="text-sm underline underline-offset-2"
                    href={agentTaskArtifactFileUrl(
                      projectId,
                      taskId,
                      artifact.revisionId,
                      file.path,
                    )}
                    download
                  >
                    {file.path} ({formatBytes(file.size)})
                  </a>
                ))}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function AgentTaskDetailPage() {
  const { taskId = "" } = useParams();
  const { project, loaded } = useCurrentProject();
  const client = useQueryClient();
  const detail = useQuery({
    ...agentTaskDetailQuery(project?.id ?? "", taskId),
    enabled: loaded && project !== null,
    refetchInterval: (query) =>
      query.state.data && activeTaskPhases.includes(query.state.data.phase) ? 10_000 : false,
  });
  const [text, setText] = useState("");
  const [confirming, setConfirming] = useState(false);
  const refresh = () => {
    void client.invalidateQueries({ queryKey: ["projects", project?.id ?? "", "agent-tasks"] });
  };
  const feedback = useMutation({
    mutationFn: () => sendAgentTaskFeedback(project?.id ?? "", taskId, text),
    onSuccess: () => {
      setText("");
      refresh();
    },
  });
  const cancel = useMutation({
    mutationFn: () => cancelAgentTask(project?.id ?? "", taskId),
    onSuccess: () => {
      setConfirming(false);
      refresh();
    },
  });
  const canWrite = project?.role === "owner" || project?.role === "developer";
  const title = "Agent task";
  const task = detail.data;

  return (
    <ProjectGate
      title={title}
      loading={!loaded || (project !== null && detail.isPending)}
      error={detail.error}
      empty={loaded && project === null}
      onRetry={() => void detail.refetch()}
    >
      <Heading title={title} />
      {task ? (
        <div className="flex flex-col gap-6">
          <Card>
            <CardContent className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <PhaseBadge phase={task.phase} />
                <span className="font-mono text-xs">{task.repositoryName}</span>
                <span className="font-mono text-xs text-muted-foreground">{task.branch}</span>
                <span className="text-xs text-muted-foreground">
                  {task.modelKeyRef
                    ? `Customer key ${task.modelKeyRef}: no credits consumed`
                    : `Credits: ${formatCredits(task.chargedCredits)} / ${formatCredits(task.creditCap)}`}
                </span>
              </div>
              {!task.live ? (
                <p className="text-sm text-muted-foreground">
                  The agent runtime could not be reached, so the live status is unavailable.
                </p>
              ) : null}
              <p className="whitespace-pre-wrap text-sm leading-relaxed">{task.prompt}</p>
            </CardContent>
          </Card>

          <section aria-labelledby="agent-turns-heading" className="flex flex-col gap-3">
            <h2 id="agent-turns-heading" className="text-base font-semibold">
              Turns
            </h2>
            {task.turns.length === 0 ? (
              <EmptyState
                title="No turns reported yet"
                description="The agent reports here when a turn finishes. Large repositories can take many minutes."
              />
            ) : (
              task.turns.map((turn) => (
                <Card key={turn.turnId}>
                  <CardContent className="flex flex-col gap-2">
                    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      <Badge variant={turn.status === "succeeded" ? "success" : "danger"}>
                        {turn.status}
                      </Badge>
                      <span>{formatZonedDateTime(turn.recordedAt, "UTC")}</span>
                      <span>
                        {formatCredits(turn.inputTokens)} in / {formatCredits(turn.outputTokens)}{" "}
                        out
                      </span>
                    </div>
                    {turn.summary ? (
                      <p className="whitespace-pre-wrap text-sm leading-relaxed">{turn.summary}</p>
                    ) : null}
                    {turn.error ? (
                      <p className="whitespace-pre-wrap text-sm text-danger">{turn.error}</p>
                    ) : null}
                  </CardContent>
                </Card>
              ))
            )}
          </section>

          {project ? (
            <TaskResults projectId={project.id} taskId={taskId} refreshKey={task.turns.length} />
          ) : null}

          {canWrite && activeTaskPhases.includes(task.phase) ? (
            <section aria-labelledby="agent-steer-heading" className="flex flex-col gap-3">
              <h2 id="agent-steer-heading" className="text-base font-semibold">
                Steer the agent
              </h2>
              <form
                className="flex flex-col gap-2"
                onSubmit={(event) => {
                  event.preventDefault();
                  if (text.trim()) feedback.mutate();
                }}
              >
                <Textarea
                  aria-label="Message to the agent"
                  value={text}
                  maxLength={20000}
                  onChange={(event) => setText(event.target.value)}
                />
                {feedback.isError ? (
                  <p role="alert" className="text-sm text-danger">
                    {problemText(feedback.error, "The message could not be sent.")}
                  </p>
                ) : null}
                <div className="flex flex-wrap gap-2">
                  <Button type="submit" disabled={!text.trim() || feedback.isPending}>
                    Send message
                  </Button>
                  {confirming ? (
                    <>
                      <Button
                        variant="outline"
                        onClick={() => cancel.mutate()}
                        disabled={cancel.isPending}
                      >
                        Confirm cancel
                      </Button>
                      <Button variant="ghost" onClick={() => setConfirming(false)}>
                        Keep running
                      </Button>
                    </>
                  ) : (
                    <Button type="button" variant="outline" onClick={() => setConfirming(true)}>
                      Cancel task
                    </Button>
                  )}
                </div>
                {cancel.isError ? (
                  <p role="alert" className="text-sm text-danger">
                    {problemText(cancel.error, "The task could not be cancelled.")}
                  </p>
                ) : null}
              </form>
            </section>
          ) : null}
        </div>
      ) : null}
    </ProjectGate>
  );
}
